import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FC, useEffect, useRef, CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion, Variants } from "framer-motion";

// Shared animation utility
const useIntersectionAnimation = (
  sectionRef: React.RefObject<HTMLElement | null>,
  elements: Array<{
    ref: React.RefObject<HTMLElement | null>;
    delay: number;
    initialStyles?: CSSProperties;
  }>,
  options: IntersectionObserverInit = { threshold: 0.1, rootMargin: "-50px" },
) => {
  useEffect(() => {
    elements.forEach(({ ref, initialStyles }) => {
      if (ref.current && initialStyles) {
        Object.assign(ref.current.style, initialStyles);
      }
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          elements.forEach(({ ref, delay }) => {
            setTimeout(() => {
              if (ref.current) {
                ref.current.style.opacity = "1";
                ref.current.style.transform = "translateY(0)";
              }
            }, delay);
          });
        }
      });
    }, options);

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, [sectionRef, elements]);
};

const CarouselComponent: FC<{
  title: string;
  items: Array<{
    img: string;
    title?: string;
    description?: string;
    location?: string;
    galleryTab?: string;
  }>;
  settings: any;
  renderItem: (item: any) => React.JSX.Element;
}> = ({ title, items, settings, renderItem }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useIntersectionAnimation(sectionRef, [
    {
      ref: titleRef,
      delay: 0,
      initialStyles: {
        opacity: "0",
        transform: "translateY(30px)",
        transition: "opacity 0.8s ease, transform 0.8s ease",
      },
    },
    {
      ref: sliderRef,
      delay: 200,
      initialStyles: {
        opacity: "0",
        transform: "translateY(40px)",
        transition: "opacity 1s ease 0.3s, transform 1s ease 0.3s",
      },
    },
  ]);

  return (
    <div
      ref={sectionRef}
      className="container mx-auto my-12 max-w-[98vw]"
      style={{ fontFamily: "Gambetta" }}
    >
      <div className="mb-12 flex items-center justify-center">
        <h1
          ref={titleRef}
          className="text-3xl lg:text-5xl"
          style={{ fontFamily: "GT-Ultra" }}
        >
          {title}
        </h1>
      </div>
      <div ref={sliderRef} className="relative">
        <Slider {...settings}>{items.map(renderItem)}</Slider>
      </div>
    </div>
  );
};

const ProjectHighlights: FC = () => {
  const settings = {
    infinite: true,
    speed: 500,
    autoplay: false,
    slidesToShow: 4,
    slidesToScroll: 4,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          dots: true,
          autoplay: true,
          autoplaySpeed: 3000,
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const items = [
    {
      img: "/assets/imgs/Projects/Phase 1/phase1b.webp",
      title: "Spread across 108 acres near Karmeta Road",
    },
    {
      img: "/assets/imgs/Ongoing Projects/1.webp",
      title: "Jain Mandir & Ram Mandir within the township",
    },
    {
      img: "/assets/imgs/Projects/StarCity/starcity3.webp",
      title: "Over 1100+ families have trusted Nakshatra Nagar",
    },
    {
      img: "/assets/imgs/Projects/StarCity/starcity4.webp",
      title:
        "Integrated community living with parks, roads, and civic amenities",
    },
  ];

  const renderItem = (item: (typeof items)[0]) => (
    <div className="px-4 focus:outline-none">
      <div className="group relative cursor-pointer overflow-hidden rounded-lg transition-shadow hover:shadow-xl hover:shadow-black/30">
        <div className="h-96 w-auto">
          <img
            src={item.img}
            alt={item.title}
            className="h-full w-full cursor-pointer object-cover transition-transform duration-500 group-hover:scale-125"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />
        <div className="absolute inset-0 flex translate-y-[70%] flex-col items-center justify-center px-4 text-center transition-all duration-500 group-hover:translate-y-[30%]">
          <h1 className="text-2xl font-bold text-white">{item.title}</h1>
          {/* <p className="mt-2 h-24 text-lg italic text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {item.description}
            </p> */}
        </div>
      </div>
    </div>
  );

  return (
    <CarouselComponent
      // title="Project highlights"
      title="Residential Plots & Modern Duplexes"
      items={items}
      settings={settings}
      renderItem={renderItem}
    />
  );
};

const formSchema = z.object({
  Timestamp: z.string(),
  Name: z.string().min(3, "Name must be at least 3 characters").max(50),
  "Contact Number": z
    .string()
    .min(10, "Contact number must be at least 10 digits")
    .max(15)
    .regex(/^\+?[\d\s-]{10,}$/, "Invalid phone number format"),
  "Tentative Budget": z
    .number({ invalid_type_error: "Budget must be a number" })
    .min(0)
    .max(1000000000),
  "Property Type": z.string().min(1, "Property Type is required"),
});

type FormData = z.infer<typeof formSchema>;

// Animation variants for bottom-to-top fade-in
const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      delay: i * 0.2,
    },
  }),
};

const elementVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      delay: i * 0.1,
    },
  }),
};

const NakshatraNagar: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      Name: "",
      "Contact Number": "",
      "Tentative Budget": undefined,
      "Property Type": "",
    },
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value?.toString() || "");
      });
      formData.append("Timestamp", new Date().toISOString());
      formData.append("FromNakshatraLandingPage", "true");

      // // Log FormData entries
      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}: ${value}`);
      // }

      const scriptURL =
        "https://script.google.com/macros/s/AKfycbwWCusrADKhfseYWDNg7iXJtWt0B_iCXEErN6Hclznk7ZpLh8znmlE5bGd4pIdqRKRdQw/exec";

      await fetch(scriptURL, { method: "POST", body: formData });
      toast.success("Form submitted successfully!");
      // @ts-ignore
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      window.dataLayer.push({
        event: "form_submission",
        conversionValue: data["Tentative Budget"],
        currency: "INR",
      });

      setTimeout(() => reset(), 100);
      navigate("/thankyou");
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error("Failed to send enquiry. Please try again.");
    }
  };

  const getInputStatus = (field: keyof FormData) =>
    errors[field]
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-[#1a4fa3]";

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      <ToastContainer />

      {/* Hero Section */}
      <motion.section
        className="relative flex min-h-[75vh] w-full items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: "url('assets/imgs/Ongoing Projects/2.webp')",
        }}
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
        custom={0} // First section
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div
          className="relative z-10 mx-auto max-w-[90vw] px-6 py-12 lg:px-8 lg:py-16 2xl:max-w-[80vw]"
          style={{ fontFamily: "Gambetta" }}
        >
          <div className="flex flex-col gap-12 lg:flex-row lg:gap-8 lg:gap-x-20">
            {/* Left Section: Title, Subtitle, CTA */}
            <motion.div
              className="text-center lg:w-1/2 lg:text-left"
              variants={elementVariants}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <motion.h1
                className="text-3xl tracking-wide text-white md:text-5xl lg:text-6xl"
                variants={elementVariants}
                custom={1}
              >
                Luxury, Comfort and Community - All in one.
              </motion.h1>
              <motion.p
                className="mt-4 text-lg text-gray-200 md:text-xl lg:text-2xl"
                variants={elementVariants}
                custom={2}
              >
                Jabalpur’s biggest integrated township that blends comfort,
                convenience, and community living situated in one of the
                fastest-growing areas of the city.
              </motion.p>
              <motion.a
                target="_blank"
                href="https://api.whatsapp.com/send/?phone=08817521921&text&type=phone_number&app_absent=0"
                className="mt-8 inline-block rounded bg-[#1a4fa3] px-8 py-3 text-lg tracking-wide text-white transition duration-300 hover:bg-[#153d8a]"
                variants={elementVariants}
                custom={3}
              >
                Enquire Now
              </motion.a>
            </motion.div>

            {/* Right Section: Form */}
            <motion.div
              className="lg:w-1/2"
              variants={elementVariants}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <motion.form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-4 rounded bg-white/40 p-4 shadow-2xl backdrop-blur-sm"
                variants={elementVariants}
                custom={2}
              >
                <input
                  type="hidden"
                  {...register("Timestamp")}
                  value={new Date().toLocaleString("en-IN", {
                    timeZone: "Asia/Kolkata",
                  })}
                />
                <motion.div variants={elementVariants} custom={3}>
                  <label className="block text-lg font-medium">Name</label>
                  <input
                    type="text"
                    {...register("Name")}
                    placeholder="Your Name"
                    className={`mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-800 placeholder-gray-400 transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] ${getInputStatus("Name")}`}
                  />
                  {errors.Name && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.Name.message}
                    </p>
                  )}
                </motion.div>
                <motion.div variants={elementVariants} custom={4}>
                  <label className="block text-lg font-medium">
                    Contact Number
                  </label>
                  <input
                    type="text"
                    {...register("Contact Number")}
                    placeholder="Your Contact Number"
                    className={`mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-800 placeholder-gray-400 transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] ${getInputStatus("Contact Number")}`}
                  />
                  {errors["Contact Number"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["Contact Number"].message}
                    </p>
                  )}
                </motion.div>
                <motion.div variants={elementVariants} custom={5}>
                  <label className="block text-lg font-medium">
                    Tentative Budget
                  </label>
                  <input
                    type="number"
                    {...register("Tentative Budget", { valueAsNumber: true })}
                    placeholder="Your Budget (in INR)"
                    className={`mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-800 placeholder-gray-400 transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] ${getInputStatus("Tentative Budget")}`}
                  />
                  {errors["Tentative Budget"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["Tentative Budget"].message}
                    </p>
                  )}
                </motion.div>
                <motion.div variants={elementVariants} custom={6}>
                  <label className="block text-lg font-medium">
                    Property Type
                  </label>
                  <select
                    {...register("Property Type")}
                    className={`mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-800 transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#1a4fa3] ${getInputStatus("Property Type")}`}
                  >
                    <option value="" disabled>
                      Select Property Type
                    </option>
                    <option value="Plot">Plot</option>
                    <option value="Flat">Flat</option>
                    <option value="Singlex">Singlex</option>
                    <option value="Duplex">Duplex</option>
                  </select>
                  {errors["Property Type"] && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors["Property Type"].message}
                    </p>
                  )}
                </motion.div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded bg-[#1a4fa3] px-6 py-3 text-lg tracking-wide text-white transition duration-300 hover:bg-[#153d8a] disabled:opacity-50"
                >
                  Submit Enquiry
                </button>
              </motion.form>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* About Nakshatra Nagar Section */}
      <motion.section
        className="mx-auto max-w-7xl px-6 py-12 lg:px-8"
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
        custom={1} // Second section
      >
        <motion.h2
          className="mb-12 text-center text-3xl text-gray-800 lg:text-5xl"
          style={{ fontFamily: "GT-Ultra" }}
          variants={elementVariants}
          custom={0}
        >
          About Nakshatra Nagar
        </motion.h2>
        <div className="flex flex-col gap-8 lg:flex-row">
          <motion.div
            className="lg:w-1/2"
            variants={elementVariants}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            <img
              src="/assets/imgs/Projects/Phase 2/phase2.webp"
              alt="Nakshatra Nagar"
              className="h-auto w-full rounded-lg object-cover"
            />
          </motion.div>
          <motion.div
            className="lg:w-1/2"
            variants={elementVariants}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <motion.p
              className="text-lg text-gray-600"
              variants={elementVariants}
              custom={3}
            >
              Nakshatra Nagar isn’t just a township—it’s a growing community
              built on trust, connection, and thoughtful living. Developed by
              Century Group, it’s one of Jabalpur’s most trusted projects,
              designed to offer a balanced and fulfilling lifestyle for modern
              families.
            </motion.p>
            <motion.p
              className="mt-4 text-lg text-gray-600"
              variants={elementVariants}
              custom={4}
            >
              Spread across 108 acres near Karmeta Road, it’s the biggest
              integrated township in Jabalpur, combining open green spaces with
              well-planned residential plots. With the trust of over 1100
              families, Nakshatra Nagar is loved for its peaceful environment
              and thoughtful touches—like the presence of both a Jain Mandir and
              a Ram Mandir within the township. Here, life moves at your
              pace—rooted in comfort, community, and a vision for a better
              tomorrow.
            </motion.p>
            <motion.p
              className="mt-4 text-lg text-gray-600"
              variants={elementVariants}
              custom={5}
            >
              Here, life moves at your pace—rooted in comfort, community, and a
              vision for a better tomorrow.
            </motion.p>
          </motion.div>
        </div>
      </motion.section>

      <ProjectHighlights />

      {/* Location Advantages Section */}
      <motion.section
        className="mx-auto max-w-[90vw] px-6 pb-12 lg:px-8 2xl:max-w-7xl"
        initial="hidden"
        animate="visible"
        variants={sectionVariants}
        custom={2} // Third section
      >
        <motion.h2
          className="mb-12 text-center text-3xl text-gray-800 lg:text-5xl"
          style={{ fontFamily: "GT-Ultra" }}
          variants={elementVariants}
          custom={0}
        >
          Location Advantages
        </motion.h2>
        <div className="flex flex-col gap-8 lg:flex-row">
          <motion.div
            className="lg:w-1/2"
            variants={elementVariants}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            <div className="relative h-0 w-full pb-[56.25%]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14666.773933479199!2d79.8959753!3d23.2178386!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3981b033c2e3a805%3A0x92bc061dec4064c1!2snakshatranagar!5e0!3m2!1sen!2sin!4v1743762243184!5m2!1sen!2sin"
                className="absolute left-0 top-0 h-full w-full rounded-lg"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </motion.div>
          <motion.div
            className="flex items-center justify-center lg:w-1/2"
            variants={elementVariants}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <motion.ul
              className="list-disc space-y-4 pl-5 text-lg text-gray-600"
              variants={elementVariants}
              custom={3}
            >
              <motion.li variants={elementVariants} custom={4}>
                Just 5 minutes from ISBT (Deendayal Bus Stand)
              </motion.li>
              <motion.li variants={elementVariants} custom={5}>
                In close proximity to leading schools and hospitals
              </motion.li>
              <motion.li variants={elementVariants} custom={6}>
                Markets and daily essentials available within the township
              </motion.li>
              <motion.li variants={elementVariants} custom={7}>
                Located in one of the fastest-developing areas of Jabalpur
              </motion.li>
              <motion.li variants={elementVariants} custom={8}>
                Strong future growth potential and infrastructure development
              </motion.li>
            </motion.ul>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default NakshatraNagar;
